import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleHelpIcon,
  CommandIcon,
  SidebarLeftIcon,
  SidebarRightIcon,
  SlidersIcon,
  ThemeIcon,
} from '@persona-id/design-tokens/icons';
import { ReactNode, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { clampScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/utils/screen-dimensions';
import type { EditorToolbarProps } from 'dashboard/components-lib/EditorToolbar';
import EditorToolbar from 'dashboard/components-lib/EditorToolbar';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';
import { StickyContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { EditorHistory, setEditorScreenDimensions } from 'dashboard/redux/slices/inquiry-template';
import { RootState } from 'dashboard/redux/store';
import {
  redo,
  undo,
  updateEditorScreenDimensions,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import { Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

export const TOOLBAR_HEIGHT = 50;
export const TOOLBAR_TRANSITION = '200ms cubic-bezier(0.470, 0.000, 0.745, 0.715)';
export const FLE_TOOLBAR_ID = 'flow-editor-toolbar';

type LeftNavProps = {
  disabled?: boolean;
  sidePanelVisible?: boolean;
  setSidePanelVisible?: (value: boolean) => void;
};

function LeftNav({ disabled, sidePanelVisible, setSidePanelVisible }: LeftNavProps) {
  return (
    <ToolbarControlButton
      id='inquiry-template-editor__toggle-navigation'
      tooltipText='Toggle left panel'
      disabled={disabled}
      active={sidePanelVisible}
      onClick={() => setSidePanelVisible?.(!sidePanelVisible)}
      icon={<SidebarLeftIcon />}
    />
  );
}

type RightNavProps = {
  disabled?: boolean;
  sidePanelVisible?: boolean;
  setSidePanelVisible?: (value: boolean) => void;
  showShortcutsModal?: () => void;
};

const RightNavEndContainer = styled.div`
  border-left: 1px solid ${Color.CoreSlate100};
  padding: 0px ${Spacing[2]};
  margin-left: ${Spacing[2]};
  margin-right: -${Spacing[2]};
`;

function RightNav({
  basePath,
  disabled,
  sidePanelVisible,
  setSidePanelVisible,
  showShortcutsModal,
}: RightNavProps & { basePath: string | undefined }) {
  const isThemeSetFrontendEnabled = useFeatureFlag('dashboard:fle-theme-set-index-and-editor');
  // todo: Link the last line below to enablement resource when ready
  const themeSetTextComponent: ReactNode = (
    <>
      <Paragraph>Theme editing has been updated!</Paragraph>
      <Paragraph>
        To make theme changes, use the Default Theme card in the{' '}
        <Link to={`${basePath}/config`}>Config Editor</Link>
      </Paragraph>
      <Paragraph>
        You can now{' '}
        <a
          href='https://help.withpersona.com/articles/6SIHupp847yaEuVMucKAff/'
          target='_blank'
          rel='noreferrer'
        >
          apply the same theme to multiple templates.
        </a>
      </Paragraph>
    </>
  );

  return (
    <>
      {basePath && (
        <ToolbarControlButton
          id={`${FLE_TOOLBAR_ID}__toggle-theme-editor`}
          tooltipText='Theme editor'
          to={`${basePath}/theme`}
          icon={<ThemeIcon />}
          text='Theme'
          isInternalLink
          {...(isThemeSetFrontendEnabled && {
            disabled: true,
            interactive: true,
            isInternalLink: false,
            to: undefined,
            tooltipText: null,
            tooltipContent: themeSetTextComponent,
          })}
        />
      )}
      {basePath && (
        <ToolbarControlButton
          id={`${FLE_TOOLBAR_ID}__toggle-config-editor`}
          tooltipText='Inquiry configurations editor'
          to={`${basePath}/config`}
          icon={<SlidersIcon />}
          text='Configure'
          isInternalLink
          ariaLabel='Go to inquiry configurations editor'
        />
      )}
      {showShortcutsModal && (
        <ToolbarControlButton
          id={`${FLE_TOOLBAR_ID}__shortcuts`}
          tooltipText='Keyboard shortcuts'
          onClick={showShortcutsModal}
          icon={<CommandIcon />}
        />
      )}
      <ToolbarControlButton
        id={`${FLE_TOOLBAR_ID}__help`}
        tooltipText='Help article'
        to='https://support.withpersona.com/hc/en-us/articles/7223385740435-Configuring-your-Template'
        icon={<CircleHelpIcon />}
      />
      {setSidePanelVisible && (
        <RightNavEndContainer>
          <ToolbarControlButton
            id={`${FLE_TOOLBAR_ID}__toggle-right-nav-panel`}
            tooltipText='Toggle right panel'
            active={sidePanelVisible}
            disabled={disabled}
            icon={<SidebarRightIcon />}
            onClick={() => setSidePanelVisible(!sidePanelVisible)}
          />
        </RightNavEndContainer>
      )}
    </>
  );
}

type InquiryTemplateEditorToolBarProps = {
  leftNavPanel?: LeftNavProps;
  rightNavPanel?: RightNavProps;
  showShortcutsModal?: () => void;
};

export default function InquiryTemplateEditorToolbar({
  leftNavPanel,
  rightNavPanel,
  showShortcutsModal,
}: InquiryTemplateEditorToolBarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const basePath = useSelector<RootState, string | null>((state) => {
    const inquiryTemplateId = state.inquiryTemplate.inquiryTemplate?.id;
    const inquiryTemplateVersionId =
      state.inquiryTemplate.inquiryTemplateTreeData?.inquiryTemplateVersion?.id;
    if (inquiryTemplateId == null || inquiryTemplateVersionId == null) return null;
    return `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}`;
  });
  const maxScreenDimensions = useSelector<RootState, ScreenDimensions>(
    (state: RootState) => state.inquiryTemplate.maxEditorScreenDimensions,
  );
  const [undoEnabled, redoEnabled] = useSelector<RootState, [boolean, boolean]>((state) => {
    const editorHistory: EditorHistory = state.inquiryTemplate.editorHistory;
    const undoEnabled = editorHistory.historyIndex > 0;
    const redoEnabled = editorHistory.historyIndex !== editorHistory.items.length - 1;
    return [undoEnabled, redoEnabled];
  });
  const controls = useMemo<EditorToolbarProps['controls']>(
    () => ({
      undo: {
        disabled: !undoEnabled,
        onClick: () => dispatch(undo(navigate)),
      },
      redo: {
        disabled: !redoEnabled,
        onClick: () => dispatch(redo()),
      },
    }),
    [redoEnabled, undoEnabled, dispatch],
  );
  // set zoom controls directly in toolbar when rendered in PanZoomBox context in order to prevent
  // InquiryTemplateFlowEditor and its children from re-rendering when zooming due to percentage changes
  const panZoomBoxControls = usePanZoomBoxControls();
  const { currentControls, zoomInput } = useMemo<{
    currentControls: EditorToolbarProps['controls'];
    zoomInput?: EditorToolbarProps['zoomInput'];
  }>(() => {
    if (panZoomBoxControls != null && panZoomBoxControls.zoomControls != null) {
      const {
        zoomControls: { zoomIn, zoomOut, percentage, minScale, maxScale, scaleOnPoint },
      } = panZoomBoxControls;
      return {
        currentControls: {
          ...controls,
          zoomIn: {
            onClick: zoomIn,
            disabled: percentage / 100 >= maxScale,
          },
          zoomOut: {
            onClick: zoomOut,
            disabled: percentage / 100 <= minScale,
          },
        },
        zoomInput: {
          percentage: percentage,
          onChange: (percentage: number) => scaleOnPoint(percentage / 100),
        },
      };
    } else {
      return { currentControls: controls };
    }
  }, [controls, panZoomBoxControls]);

  const handleScreenDimensionsChange = useCallback(
    (requestedScreenDimensions: ScreenDimensions) => {
      dispatch(
        updateEditorScreenDimensions(
          maxScreenDimensions
            ? // If we're in screen editor, limit screen dimension changes
              clampScreenDimensions(requestedScreenDimensions, maxScreenDimensions)
            : clampScreenDimensions(requestedScreenDimensions),
        ),
      );
    },
    [dispatch, setEditorScreenDimensions, maxScreenDimensions],
  );

  return (
    <StickyContainer>
      <EditorToolbar
        id={FLE_TOOLBAR_ID}
        leftNavItems={leftNavPanel && <LeftNav {...leftNavPanel} />}
        rightNavItems={
          basePath != null && <RightNav {...{ ...rightNavPanel, basePath, showShortcutsModal }} />
        }
        controls={currentControls}
        zoomInput={zoomInput}
        screenDimensionsInput={{
          handleScreenDimensionsChange,
        }}
      />
    </StickyContainer>
  );
}
